import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  handleCreateJob,
  handleGetJob,
  handleGetJobFiles,
  handleGetJobFolders,
  handleGetJobs,
  handleGetJobsBoard,
  handleGetJobsList,
  handleGetNoteList,
  handleGetTemplate,
} from './jobThunk';
import { IJobFile, IJobs, IJobsBoard, ITemplate } from '~/utils/interface/job';
import { StatusMode, StatusModeInfo } from '~/utils/types/job';
import { DEFAULT_PAGE } from '~/utils/constants/jobs';
import { ROWS_PER_PAGE } from '~/utils/constants/common';
import { SortField } from '~/utils/types/common';
import { INote } from '~/utils/interface/note';
import { IFolder } from '~/utils/interface/folder';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { labelToKey } from '~/utils/helper';

type StatusModeState = StatusMode | 'all';

export interface JobState {
  jobs: Array<IJobs>;
  countsJobPerStatusMode: {
    all: number;
    progress: number;
    hold: number;
    completed: number;
    [key: string]: number;
  };
  statusMode: StatusModeState;
  currentPage: number;
  totalPages: number;
  selectedIds: Array<string>;
  menuSelectedId: string | undefined;
  openAlertDelete: boolean;
  openAlertDeleteFolder: boolean;
  needRefreshData: boolean;
  filter: IJobFilter;
  filterList: IJobFilter;
  filterBoard: IJobFilterBoard;
  files: Array<IJobFile>;
  filterFile: IJobFileFilter;
  jobsBoard: Array<IJobsBoard>;
  jobsList: Array<IJobsBoard>;
  notesList: Array<INote>;
  templateList: Array<ITemplate>;
  folders: Array<IFolder>;
  filterFolder: IFilterFolder;
  menuSelectedFolderId: string | undefined;
  selectedFolderIds: Array<string>;

  scheduleCurrentTime: string;
  needRefreshScheduleData: boolean;
  job: any;
  groupedMoisturePoints: GroupedMoisturePoint[];
}

interface IJobFilterBoard {
  search?: string;
  searchFields?: Array<string>;
  limit?: number;
  type?: string;
  page?: number;
  jobTypeId?: string;
  assigneeIds?: Array<string>;
  status?: StatusModeInfo;
  mode?: 'full' | 'me';
}

interface IJobFilter {
  status?: StatusModeInfo | Array<StatusModeInfo>;
  search?: string;
  searchFields?: Array<string>;
  sort?: Array<SortField>;
  page?: number;
  limit?: number;
  assigneeIds?: Array<string>;
  jobTypeId?: string;
  mode?: 'full' | 'me';
}
interface IJobFileFilter {
  page?: number;
  limit?: number;
  selectedTags?: Array<string>;
}

interface IFilterFolder {
  page?: number;
  limit?: number;
  type?: string;
}
interface GroupedMoisturePoint {
  area: string;
  key: string;
  items: any[];
}

const initialState: JobState = {
  jobs: [],
  job: undefined,
  jobsBoard: [],
  templateList: [],
  jobsList: [],
  notesList: [],
  countsJobPerStatusMode: {
    all: 0,
    progress: 0,
    hold: 0,
    completed: 0,
  },
  statusMode: 'all',
  currentPage: DEFAULT_PAGE,
  totalPages: 0,
  menuSelectedId: undefined,
  selectedIds: [],
  openAlertDelete: false,
  openAlertDeleteFolder: false,
  needRefreshData: false,
  filter: {
    status: undefined,
    search: '',
    searchFields: ['name'],
    sort: [],
    page: 1,
    limit: ROWS_PER_PAGE,
    assigneeIds: [],
    jobTypeId: undefined,
  },

  filterBoard: {
    search: '',
    searchFields: ['name'],
  },
  filterList: {
    status: undefined,
    search: '',
    searchFields: ['name'],
    sort: [],
    page: 1,
    limit: ROWS_PER_PAGE,
  },
  filterFile: {
    page: 1,
    limit: ROWS_PER_PAGE,
    selectedTags: [],
  },
  files: [],
  folders: [],
  filterFolder: {
    type: 'jobs',
  },
  menuSelectedFolderId: undefined,
  selectedFolderIds: [],

  // Schedule in job detail
  scheduleCurrentTime: dayjs().format('YYYY-MM-DD'),
  needRefreshScheduleData: false,
  groupedMoisturePoints: [],
};

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    changeStatusMode(state, action: PayloadAction<StatusModeState>) {
      state.statusMode = action.payload;
    },
    setFilter(state, action: PayloadAction<IJobFilter>) {
      state.filter = { ...state.filter, ...action.payload };
    },
    setFilterList(state, action: PayloadAction<IJobFilter>) {
      state.filterList = { ...state.filterList, ...action.payload };
    },
    setFilterBoard(state, action: PayloadAction<IJobFilterBoard>) {
      state.filterBoard = { ...state.filterBoard, ...action.payload };
    },
    setFilterFile(state, action: PayloadAction<IJobFileFilter>) {
      state.filterFile = { ...state.filterFile, ...action.payload };
    },
    setFilterFolder(state, action: PayloadAction<IFilterFolder>) {
      state.filterFolder = { ...state.filterFolder, ...action.payload };
    },
    setJobs(state, action: PayloadAction<Array<IJobs>>) {
      state.jobs = [...action.payload];
    },
    setScheduleCurrentTime(state, action: PayloadAction<string>) {
      state.scheduleCurrentTime = action.payload;
    },
    setNeedRefreshScheduleData(state, action: PayloadAction<boolean>) {
      state.needRefreshScheduleData = action.payload;
    },
    changeSelectedIds(state, action: PayloadAction<Array<string>>) {
      state.selectedIds = [...action.payload];
    },
    changeSelectedFolderIds(state, action: PayloadAction<Array<string>>) {
      state.selectedFolderIds = [...action.payload];
    },
    changeOpenAlertDelete(state, action: PayloadAction<boolean>) {
      state.openAlertDelete = action.payload;
    },
    changeOpenAlertDeleteFolder(state, action: PayloadAction<boolean>) {
      state.openAlertDeleteFolder = action.payload;
    },
    changeMenuSelectId(state, action: PayloadAction<string | undefined>) {
      state.menuSelectedId = action.payload;
    },
    changeMenuSelectFolderId(state, action: PayloadAction<string | undefined>) {
      state.menuSelectedFolderId = action.payload;
    },
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleGetJobs.pending, (state, action) => {
        state.selectedIds = [];
      })
      .addCase(handleGetJobs.fulfilled, (state, action) => {
        state.jobs = [...action.payload.responses];
        state.countsJobPerStatusMode = {
          all: action.payload.countAll,
          progress: action.payload.countInProgress,
          hold: action.payload.countHold,
          completed: action.payload.countCompleted,
        };
        state.currentPage = action.payload.pagination.page;
        state.totalPages = action.payload.pagination.totalPages;
        state.needRefreshData = false;
      })
      .addCase(handleGetJobs.rejected, (state, action) => {})
      .addCase(handleGetJobFiles.fulfilled, (state, action) => {
        state.files = [...action.payload.responses];

        state.currentPage = action.payload.pagination.page;

        state.totalPages = action.payload.pagination.totalPages;
        state.needRefreshData = false;
      })
      .addCase(handleGetJobsBoard.fulfilled, (state, action) => {
        state.jobsBoard = [...action.payload.responses];
        state.needRefreshData = false;
      })
      .addCase(handleGetJobsList.fulfilled, (state, action) => {
        state.jobsBoard = [...state.jobsBoard, ...action.payload.responses];
        state.needRefreshData = false;
      })
      .addCase(handleGetNoteList.fulfilled, (state, action) => {
        state.notesList = [...action.payload.responses];
        state.needRefreshData = false;
      })
      .addCase(handleGetTemplate.fulfilled, (state, action) => {
        state.templateList = [...action.payload.responses];
        state.needRefreshData = false;
      })
      .addCase(handleGetJobFolders.fulfilled, (state, action) => {
        state.folders = [...(action.payload.responses || action.payload)];
        state.needRefreshData = false;
      })
      .addCase(handleGetJob.fulfilled, (state, action) => {
        const payload = action.payload ? cloneDeep(action.payload) : null;

        state.job = {
          ...payload,
          tagIds: payload?.tags?.map((t: any) => t.id) ?? [],
        };

        state.groupedMoisturePoints = Array.isArray(payload?.moisturePoint?.moisturePoints)
          ? payload.moisturePoint.moisturePoints.reduce((acc: any[], item: any, index: number) => {
              let group = acc.find((g) => g.area === item.area);

              if (!group) {
                group = {
                  area: item.area,
                  items: [],
                  key: labelToKey(item.area ?? ''),
                };
                acc.push(group);
              }

              group.items.push({
                ...item,
                id: index,
              });

              return acc;
            }, [])
          : [];
      });

    builder.addCase(handleCreateJob.fulfilled, (state, action) => {});
  },
});

export const jobActions = jobSlice.actions;

const jobReducer = jobSlice.reducer;
export default jobReducer;
